import { useEffect, useRef, useState } from "react";
import {
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  formatApiError,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  UsersThree,
  Plus,
  PencilSimple,
  Trash,
  Check,
  X,
  Key,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["admin", "user"];

export default function UserManagementPage() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // username or "__new__"
  const [draft, setDraft] = useState(null);
  const [resetingPwd, setResetingPwd] = useState(null); // username of user being password-reset
  const [newPwd, setNewPwd] = useState("");

  const load = () => listUsers().then(setRows);
  useEffect(() => {
    load();
  }, []);

  const startAdd = () => {
    setEditing("__new__");
    setDraft({ username: "", password: "", name: "", role: "user" });
  };

  const startEdit = (r) => {
    setEditing(r.username);
    setDraft({ ...r, password: "" });
  };

  const cancel = () => {
    setEditing(null);
    setDraft(null);
  };

  const save = async () => {
    try {
      if (editing === "__new__") {
        await createUser(draft);
        toast.success("User dibuat.");
      } else {
        const patch = { name: draft.name, role: draft.role };
        await updateUser(editing, patch);
        toast.success("User diupdate.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (username) => {
    if (!window.confirm(`Hapus user "${username}"?`)) return;
    try {
      await deleteUser(username);
      toast.success("User dihapus.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doResetPwd = async () => {
    if (!newPwd || newPwd.length < 6) {
      toast.error("Password minimal 6 karakter.");
      return;
    }
    try {
      await updateUser(resetingPwd, { password: newPwd });
      toast.success(`Password ${resetingPwd} di-reset.`);
      setResetingPwd(null);
      setNewPwd("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-5xl">
      <div className="bg-white border border-slate-200 rounded shadow-sm p-5">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
          User Management
        </div>
        <div className="text-xl md:text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <UsersThree size={22} weight="duotone" /> Kelola Akun Admin & Staff
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Admin terakhir tidak bisa dihapus atau di-demote. Anda tidak bisa hapus akun
          sendiri.
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="flex items-center justify-end p-4 border-b border-slate-100">
          <button
            onClick={startAdd}
            className="bg-[#0A3F9A] text-white px-3 py-2 rounded text-sm font-medium flex items-center gap-2"
            data-testid="btn-add-user"
          >
            <Plus size={14} /> Tambah User
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b text-xs">
                  Username
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b text-xs">
                  Nama
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b text-xs">
                  Role
                </th>
                <th className="bg-slate-50 text-slate-600 font-semibold text-right p-3 border-b text-xs">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody>
              {editing === "__new__" && draft && (
                <tr className="bg-yellow-50">
                  <td className="p-3 border-b">
                    <input
                      type="text"
                      value={draft.username}
                      onChange={(e) => setDraft({ ...draft, username: e.target.value })}
                      placeholder="username (min 3)"
                      className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                      data-testid="edit-user-username"
                    />
                    <input
                      type="password"
                      value={draft.password}
                      onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                      placeholder="password (min 6)"
                      className="w-full mt-1 border border-slate-300 rounded px-2 py-1 text-xs"
                      data-testid="edit-user-password"
                    />
                  </td>
                  <td className="p-3 border-b">
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                      placeholder="Nama tampilan"
                      className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                      data-testid="edit-user-name"
                    />
                  </td>
                  <td className="p-3 border-b">
                    <select
                      value={draft.role}
                      onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                      className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                      data-testid="edit-user-role"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-3 border-b text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={save}
                        className="text-white bg-[#057A55] p-1 rounded"
                        data-testid="btn-save-user"
                      >
                        <Check size={14} weight="bold" />
                      </button>
                      <button onClick={cancel} className="text-slate-600 p-1 rounded">
                        <X size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const isEditing = editing === r.username;
                const isSelf = me?.username === r.username;
                if (isEditing && editing !== "__new__") {
                  return (
                    <tr key={r.username} className="bg-yellow-50">
                      <td className="p-3 border-b text-sm">{r.username}</td>
                      <td className="p-3 border-b">
                        <input
                          type="text"
                          value={draft.name || ""}
                          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                        />
                      </td>
                      <td className="p-3 border-b">
                        <select
                          value={draft.role}
                          onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                          className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                        >
                          {ROLES.map((rl) => (
                            <option key={rl} value={rl}>
                              {rl}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="p-3 border-b text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={save}
                            className="text-white bg-[#057A55] p-1 rounded"
                          >
                            <Check size={14} weight="bold" />
                          </button>
                          <button onClick={cancel} className="text-slate-600 p-1 rounded">
                            <X size={14} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr
                    key={r.username}
                    className="hover:bg-slate-50"
                    data-testid={`row-user-${r.username}`}
                  >
                    <td className="p-3 border-b text-sm font-medium">
                      {r.username}
                      {isSelf && (
                        <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          Anda
                        </span>
                      )}
                    </td>
                    <td className="p-3 border-b text-sm text-slate-700">
                      {r.name || "-"}
                    </td>
                    <td className="p-3 border-b">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase ${
                          r.role === "admin"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {r.role}
                      </span>
                    </td>
                    <td className="p-3 border-b text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => {
                            setResetingPwd(r.username);
                            setNewPwd("");
                          }}
                          className="text-slate-600 hover:text-[#D97706] p-1"
                          title="Reset password"
                          data-testid={`btn-reset-pwd-${r.username}`}
                        >
                          <Key size={14} weight="duotone" />
                        </button>
                        <button
                          onClick={() => startEdit(r)}
                          className="text-slate-600 hover:text-[#0A3F9A] p-1"
                          data-testid={`btn-edit-user-${r.username}`}
                        >
                          <PencilSimple size={14} weight="duotone" />
                        </button>
                        <button
                          onClick={() => remove(r.username)}
                          disabled={isSelf}
                          className="text-slate-600 hover:text-red-600 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`btn-delete-user-${r.username}`}
                        >
                          <Trash size={14} weight="duotone" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {resetingPwd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-lg max-w-md w-full p-6">
            <div className="text-lg font-bold text-slate-900 mb-1">
              Reset Password
            </div>
            <div className="text-sm text-slate-600 mb-4">
              User: <code className="bg-slate-100 px-1 py-0.5 rounded">{resetingPwd}</code>
            </div>
            <input
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="Password baru (min 6 karakter)"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              data-testid="input-reset-password"
              autoFocus
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setResetingPwd(null);
                  setNewPwd("");
                }}
                className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded"
              >
                Batal
              </button>
              <button
                onClick={doResetPwd}
                className="bg-[#0A3F9A] text-white px-4 py-2 rounded text-sm font-medium"
                data-testid="btn-confirm-reset-pwd"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
